import React, { useCallback, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import useAuthenticatedFetch from "../../hooks/useAuthenticatedFetch";

export default function ProRequestForm({ communityId, onSubmitted }) {
  const { t } = useTranslation();
  const fetchWithAuth = useAuthenticatedFetch();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      setError("");
      setSuccess(false);

      if (!communityId) {
        setError(t("billing.proRequest.errors.missingCommunity"));
        return;
      }

      try {
        setSubmitting(true);
        const res = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/billing/pro-request`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ communityId }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(json?.error || json?.message || t("billing.proRequest.errors.submit"));
        }

        setSuccess(true);
        onSubmitted?.();
      } catch (e) {
        setError(e?.message || String(e));
      } finally {
        setSubmitting(false);
      }
    },
    [communityId, fetchWithAuth, onSubmitted, t]
  );

  return (
    <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {success ? <Alert severity="success">{t("billing.proRequest.success")}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Typography variant="body2" color="text.secondary">
        {t("billing.proRequest.oneClickInfo")}
      </Typography>

      <Button type="submit" variant="contained" disabled={submitting || !communityId}>
        {submitting ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} color="inherit" />
            {t("billing.proRequest.sending")}
          </Box>
        ) : (
          t("billing.proRequest.submit")
        )}
      </Button>
    </Box>
  );
}
